import React, { useRef, useState, forwardRef, useImperativeHandle, useContext, useEffect } from 'react'

import { classNames } from 'primereact/utils'
import { Dialog } from 'primereact/dialog'
import { Dropdown } from 'primereact/dropdown'
import { Calendar } from 'primereact/calendar'
import { InputText } from 'primereact/inputtext'
import { Button } from 'primereact/button'
import { InputNumber } from 'primereact/inputnumber'
import { Toast } from 'primereact/toast'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'

import server from '../../../server'

import { ToastPopUp } from '../../shared/utils'
import { formatDate } from '../../shared/utils'
import { AuthenticationContext } from '../../../Auth/authentication.context'
import useTableRowsPerPage from '../../../hooks/useTableRowsPerPage'
import { getInitialPaginationState } from '../../../shared/tablePagination'

const AddEditMolecule = forwardRef((props, ref) => {
  const { AddEditRef } = ref
  const { molecule, setMolecule, emptyMolecule } = props
  const [moleculeDialog, setMoleculeDialog] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [isMoleculeEdit, setIsMoleculeEdit] = useState(false)
  const [upcSearchValue, setUpcSearchValue] = useState('')
  const [upcSearchLoading, setUpcSearchLoading] = useState(false)
  const [upcsDialogVisible, setUpcsDialogVisible] = useState(false)
  const [relatedProducts, setRelatedProducts] = useState([])
  const [searchUpcsLoading, setSearchUpcsLoading] = useState(false)
  const [searchResultsDialogVisible, setSearchResultsDialogVisible] = useState(false)
  const [searchResults, setSearchResults] = useState([])

  const { user } = useContext(AuthenticationContext)
  const { defaultRows, rowsPerPageOptions, applyPageEvent } = useTableRowsPerPage()
  const [relatedPg, setRelatedPg] = useState(() => getInitialPaginationState(user))
  const [searchPg, setSearchPg] = useState(() => getInitialPaginationState(user))

  useEffect(() => {
    setRelatedPg((prev) => ({ ...prev, rows: defaultRows }))
    setSearchPg((prev) => ({ ...prev, rows: defaultRows }))
  }, [defaultRows])

  const toast = useRef(null)

  useImperativeHandle(AddEditRef, () => ({
    openNew() {
      setSubmitted(false)
      setMoleculeDialog(true)
      setMolecule(emptyMolecule)
      setUpcSearchValue('')
      setRelatedProducts([])
      setSearchResults([])
      setUpcsDialogVisible(false)
      setSearchResultsDialogVisible(false)
    },

    editMolecule(molecule) {
      setSubmitted(false)
      setIsMoleculeEdit(true)
      // Format DIN with leading zeros for display
      const formattedMolecule = {
        ...molecule,
        name: molecule.name || '',
        drugIdentificationNo: molecule.drugIdentificationNo 
          ? String(molecule.drugIdentificationNo).padStart(8, '0')
          : null
      }
      setMolecule(formattedMolecule)
      setUpcSearchValue('')
      setRelatedProducts([])
      setSearchResults([])
      setUpcsDialogVisible(false)
      setSearchResultsDialogVisible(false)
      setMoleculeDialog(true)
    },
  }))

  const hideDialog = () => {
    setMoleculeDialog(false)
    setIsMoleculeEdit(false)
    setUpcSearchValue('')
    setRelatedProducts([])
    setSearchResults([])
    setUpcsDialogVisible(false)
    setSearchResultsDialogVisible(false)
  }

  // Listens to changes inside Diloge modal
  const onInputChange = (e, name) => {
    const val = (e.target && e.target.value) || ''
    let _molecule = { ...molecule }

    _molecule[`${name}`] = val

    setMolecule(_molecule)
  }

  const onInputNumberChange = (e, name) => {
    const val = e.value || 0
    let _molecule = { ...molecule }

    _molecule[`${name}`] = val

    setMolecule(_molecule)
  }

  // Handle DIN input with leading zeros support and 8-digit limit
  const onDINChange = (e) => {
    const val = (e.target && e.target.value) || ''
    // Only allow digits and limit to 8 characters
    const numericValue = val.replace(/\D/g, '').slice(0, 8)
    let _molecule = { ...molecule }
    _molecule.drugIdentificationNo = numericValue === '' ? null : numericValue
    setMolecule(_molecule)
  }

  // Search for UPCs by DIN
  const searchUpcsByDin = async () => {
    if (!molecule.drugIdentificationNo || String(molecule.drugIdentificationNo).length !== 8) {
      ToastPopUp(toast, 'warn', 'Invalid DIN', 'Please enter a valid 8-digit DIN first.')
      return
    }

    setSearchUpcsLoading(true)
    try {
      // Normalize DIN (remove leading zeros for search)
      const dinValue = parseInt(molecule.drugIdentificationNo, 10)
      
      const response = await server.get('/products/', {
        params: {
          q: String(dinValue),
          page: 1,
          limit: 100, // Get up to 100 products
        },
      })

      // Filter products that match the exact DIN
      const matchingProducts = response.data.data.filter(
        (product) => product.drugIdentificationNo === dinValue
      )
      
      if (matchingProducts.length === 0) {
        ToastPopUp(toast, 'info', 'No UPCs Found', `No UPCs found for DIN ${String(molecule.drugIdentificationNo).padStart(8, '0')}`)
      } else {
        // Store full product information for display
        setRelatedProducts(matchingProducts)
        setUpcsDialogVisible(true)
      }
    } catch (error) {
      ToastPopUp(toast, 'error', 'Search Error', error.response?.data?.message || 'Failed to search for UPCs')
    } finally {
      setSearchUpcsLoading(false)
    }
  }

  // Search for DIN by UPC or description
  const searchDinByUpc = async () => {
    if (!upcSearchValue.trim()) {
      ToastPopUp(toast, 'warn', 'Invalid Search', 'Please enter a UPC or medication description to search.')
      return
    }

    setUpcSearchLoading(true)
    try {
      const response = await server.get('/products/', {
        params: {
          q: upcSearchValue.trim(),
          page: 1,
          limit: 50, // Increased limit to show more results
        },
      })

      // Find products matching the UPC or description
      const searchTerm = upcSearchValue.trim().toLowerCase()
      const matchingProducts = response.data.data.filter(
        (product) => 
          product.retailUpcCode?.toLowerCase().includes(searchTerm) ||
          product.itemDescription?.toLowerCase().includes(searchTerm) ||
          product.item_description?.toLowerCase().includes(searchTerm)
      )

      if (matchingProducts.length === 0) {
        ToastPopUp(toast, 'info', 'No Results', `No products found matching "${upcSearchValue.trim()}"`)
      } else {
        // Show results in dialog
        setSearchResults(matchingProducts)
        setSearchResultsDialogVisible(true)
      }
    } catch (error) {
      ToastPopUp(toast, 'error', 'Search Error', error.response?.data?.message || 'Failed to search for DIN')
    } finally {
      setUpcSearchLoading(false)
    }
  }

  // Handle product selection from search results
  const handleProductSelect = (product) => {
    if (product.drugIdentificationNo) {
      // Format DIN with leading zeros
      const formattedDin = String(product.drugIdentificationNo).padStart(8, '0')
      let _molecule = { ...molecule }
      _molecule.drugIdentificationNo = formattedDin
      setMolecule(_molecule)
      ToastPopUp(toast, 'success', 'DIN Selected', `DIN ${formattedDin} has been selected`)
      setSearchResultsDialogVisible(false)
      setUpcSearchValue('') // Clear search field
    } else {
      ToastPopUp(toast, 'warn', 'No DIN', 'This product does not have a DIN associated')
    }
  }

  // Handles saving new molecule and editing it
  const saveMolecule = () => {
    // Validate DIN is exactly 8 digits
    const dinValid = molecule.drugIdentificationNo && 
                     String(molecule.drugIdentificationNo).length === 8
    
    if (
      dinValid &&
      molecule.source &&
      molecule.paPercentage != null &&
      molecule.startDate
    ) {
      // Format date properly for backend (ISO format or YYYY-MM-DD)
      let formattedStartDate = formatDate(molecule.startDate)
      // Ensure proper date format with zero-padding
      if (formattedStartDate) {
        const dateParts = formattedStartDate.split('-')
        if (dateParts.length === 3) {
          const year = dateParts[0]
          const month = String(dateParts[1]).padStart(2, '0')
          const day = String(dateParts[2]).padStart(2, '0')
          formattedStartDate = `${year}-${month}-${day}`
        }
      }

      const _molecule = {
        // Use provided name or fallback to auto-generated name
        name: molecule.name && molecule.name.trim() 
          ? molecule.name.trim() 
          : `Molecule ${String(molecule.drugIdentificationNo).padStart(8, '0')}`,
        source: molecule.source,
        // Convert DIN to number for backend (it will be stored as number)
        drugIdentificationNo: parseInt(molecule.drugIdentificationNo, 10),
        // Ensure paPercentage is a number
        paPercentage: Number(molecule.paPercentage),
        // Format date as string (backend Joi.date() will parse it)
        startDate: formattedStartDate,
      }

      server
        .post('/molecules/', _molecule)
        .then((response) => {
          props.getData()
          if (isMoleculeEdit) {
            ToastPopUp(
              toast,
              'success',
              'Molecule Updated',
              'Molecule has been Updated successfully.'
            )
          } else {
            ToastPopUp(
              toast,
              'success',
              'Molecule Added',
              'Molecule has been added successfully.'
            )
          }
          setIsMoleculeEdit(false)
          setSubmitted(true)
        })
        .catch((e) => {
          ToastPopUp(toast, 'error', 'Error', e.response.data.message)
          setIsMoleculeEdit(false)
          setSubmitted(true)
        })

      setMoleculeDialog(false)
      setMolecule(emptyMolecule)
    } else {
      setSubmitted(true)
    }
  }

  //  Dialog footers
  const moleculeDialgeFooter = (
    <React.Fragment>
      <Button label='Cancel' icon='pi pi-times' outlined onClick={hideDialog} />
      <Button
        label={isMoleculeEdit ? 'Update Molecule' : 'Add New Molecule'}
        icon='pi pi-check'
        onClick={saveMolecule}
      />
    </React.Fragment>
  )

  return (
    <>
      <div className='card flex justify-content-center'>
        <Toast ref={toast} />
      </div>
      {/* dialog for creation and edit molecule */}
      <Dialog
        visible={moleculeDialog}
        style={{ width: '32rem' }}
        breakpoints={{ '960px': '75vw', '641px': '90vw' }}
        header={isMoleculeEdit ? 'Update Molecule' : 'Add New Molecule'}
        modal
        className='p-fluid'
        footer={moleculeDialgeFooter}
        onHide={hideDialog}
      >
        <div className='field'>
          <label htmlFor='upcSearch' className='font-bold'>
            Search by UPC or Description
          </label>
          <div className='d-flex gap-2'>
            <InputText
              id='upcSearch'
              value={upcSearchValue}
              onChange={(e) => setUpcSearchValue(e.target.value)}
              placeholder='Enter UPC or medication description'
              className='flex-grow-1'
              disabled={isMoleculeEdit}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !isMoleculeEdit) {
                  searchDinByUpc()
                }
              }}
            />
            <Button
              icon='pi pi-search'
              onClick={searchDinByUpc}
              disabled={isMoleculeEdit || upcSearchLoading || !upcSearchValue.trim()}
              loading={upcSearchLoading}
              tooltip='Search for DIN by UPC or medication description'
              tooltipOptions={{ position: 'top' }}
            />
          </div>
        </div>
        <div className='field mt-4'>
          <label htmlFor='name' className='font-bold'>
            Molecule Name
          </label>
          <InputText
            id='name'
            value={molecule.name || ''}
            onChange={(e) => {
              onInputChange(e, 'name')
            }}
            placeholder='Enter molecule name'
            className='w-full'
          />
        </div>
        <div className='field mt-4'>
          <label htmlFor='drugIdentificationNo' className='font-bold'>
            Molecule DIN
          </label>
          <div className='d-flex gap-2'>
            <InputText
              id='drugIdentificationNo'
              value={molecule.drugIdentificationNo || ''}
              autoFocus
              onChange={onDINChange}
              placeholder='Type DIN (8 digits)'
              maxLength={8}
              className={classNames(
                `flex-grow-1 ${isMoleculeEdit && 'disabledInput'}`
              )}
              readOnly={isMoleculeEdit ? true : false}
            />
            <Button
              icon='pi pi-search'
              onClick={searchUpcsByDin}
              disabled={isMoleculeEdit || searchUpcsLoading || !molecule.drugIdentificationNo || String(molecule.drugIdentificationNo).length !== 8}
              loading={searchUpcsLoading}
              tooltip='Search for UPCs by DIN'
              tooltipOptions={{ position: 'top' }}
            />
          </div>
          {submitted && !molecule.drugIdentificationNo && (
            <small className='p-error'>Din is required.</small>
          )}
          {submitted && molecule.drugIdentificationNo && String(molecule.drugIdentificationNo).length !== 8 && (
            <small className='p-error'>DIN must be exactly 8 digits.</small>
          )}
          {!submitted && molecule.drugIdentificationNo && String(molecule.drugIdentificationNo).length < 8 && (
            <small className='p-text-secondary'>Enter 8 digits (leading zeros allowed)</small>
          )}
        </div>
        <div className='field mt-4'>
          <label htmlFor='source' className='font-bold'>
            Molecule Source Type
          </label>
          <Dropdown
            id='source'
            value={molecule.source}
            onChange={(e) => {
              onInputChange(e, 'source')
            }}
            options={[
              { label: 'Single', value: 'single' },
              { label: 'Dual', value: 'dual' },
              { label: 'Multi', value: 'multi' },
            ]}
            optionLabel='label'
            placeholder='Select Source Type'
            // className='w-full md:w-14rem'
            readOnly={isMoleculeEdit ? true : false}
            className={classNames(
              `w-full md:w-14rem ${isMoleculeEdit && 'disabledInput'}`
            )}
          />
          {submitted && !molecule.source && (
            <small className='p-error'>Source type is required.</small>
          )}
        </div>
        <div className='field mt-4'>
          <label htmlFor='paPercentage' className='font-bold'>
            Molecule PA %
          </label>
          <InputNumber
            id='paPercentage'
            value={molecule.paPercentage}
            onChange={(e) => {
              onInputNumberChange(e, 'paPercentage')
            }}
            placeholder='Type PA %'
            className='w-full md:w-14rem'
          />
          {submitted && !molecule.paPercentage && (
            <small className='p-error'>PA is required.</small>
          )}
        </div>
        <div className='field mt-4'>
          <label htmlFor='startDate' className='font-bold'>
            Start Date
          </label>
          <Calendar
            id='startDate'
            value={molecule.startDate}
            onChange={(e) => onInputChange(e, 'startDate')}
            dateFormat='yy-mm-dd'
            placeholder='yy-mm-dd'
            mask='99/99/99'
            showIcon
            showButtonBar
            minDate={new Date()}
          />
          {submitted && !molecule.startDate && (
            <small className='p-error'>Start Date is required.</small>
          )}
        </div>
      </Dialog>

      {/* Dialog to show related UPCs */}
      <Dialog
        visible={upcsDialogVisible}
        style={{ width: '50rem' }}
        breakpoints={{ '960px': '75vw', '641px': '90vw' }}
        header={`Products for DIN ${molecule.drugIdentificationNo ? String(molecule.drugIdentificationNo).padStart(8, '0') : ''}`}
        modal
        onHide={() => setUpcsDialogVisible(false)}
        footer={
          <Button
            label='Close'
            icon='pi pi-times'
            onClick={() => setUpcsDialogVisible(false)}
          />
        }
      >
        <div className='field'>
          {relatedProducts.length > 0 ? (
            <DataTable
              value={relatedProducts}
              emptyMessage='No products found'
              paginator
              rows={relatedPg.rows}
              first={relatedPg.first}
              onPage={(e) => setRelatedPg((prev) => applyPageEvent(prev, e))}
              rowsPerPageOptions={rowsPerPageOptions}
              showGridlines
            >
              <Column field='retailUpcCode' header='UPC Code' style={{ minWidth: '12rem' }} />
              <Column 
                field='itemDescription' 
                header='Description' 
                style={{ minWidth: '20rem' }}
                body={(rowData) => rowData.itemDescription || rowData.item_description || 'N/A'}
              />
              <Column 
                field='manufacturer.name' 
                header='Manufacturer' 
                style={{ minWidth: '15rem' }}
                body={(rowData) => rowData.manufacturer?.name || 'N/A'}
              />
            </DataTable>
          ) : (
            <p>No products found for this DIN.</p>
          )}
        </div>
      </Dialog>

      {/* Dialog to show search results */}
      <Dialog
        visible={searchResultsDialogVisible}
        style={{ width: '70rem' }}
        breakpoints={{ '960px': '75vw', '641px': '90vw' }}
        header={`Search Results for "${upcSearchValue}"`}
        modal
        onHide={() => setSearchResultsDialogVisible(false)}
        footer={
          <Button
            label='Close'
            icon='pi pi-times'
            onClick={() => setSearchResultsDialogVisible(false)}
          />
        }
      >
        <div className='field'>
          {searchResults.length > 0 ? (
            <DataTable
              value={searchResults}
              emptyMessage='No products found'
              paginator
              rows={searchPg.rows}
              first={searchPg.first}
              onPage={(e) => setSearchPg((prev) => applyPageEvent(prev, e))}
              rowsPerPageOptions={rowsPerPageOptions}
              showGridlines
            >
              <Column field='retailUpcCode' header='UPC Code' style={{ minWidth: '12rem' }} />
              <Column 
                field='itemDescription' 
                header='Description' 
                style={{ minWidth: '25rem' }}
                body={(rowData) => rowData.itemDescription || rowData.item_description || 'N/A'}
              />
              <Column 
                field='manufacturer.name' 
                header='Manufacturer' 
                style={{ minWidth: '15rem' }}
                body={(rowData) => rowData.manufacturer?.name || 'N/A'}
              />
              <Column 
                field='drugIdentificationNo' 
                header='DIN' 
                style={{ minWidth: '10rem' }}
                body={(rowData) => 
                  rowData.drugIdentificationNo 
                    ? String(rowData.drugIdentificationNo).padStart(8, '0')
                    : 'N/A'
                }
              />
              <Column 
                header='Action' 
                style={{ minWidth: '10rem' }}
                body={(rowData) => (
                  <Button
                    label='Select'
                    icon='pi pi-check'
                    size='small'
                    onClick={() => handleProductSelect(rowData)}
                    disabled={!rowData.drugIdentificationNo}
                    severity={rowData.drugIdentificationNo ? 'success' : 'secondary'}
                  />
                )}
              />
            </DataTable>
          ) : (
            <p>No products found.</p>
          )}
        </div>
      </Dialog>
    </>
  )
})

export default AddEditMolecule
